import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Badge, IconButton, Popover, List, ListItem, ListItemText, Typography, Box, Button, Divider } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import { fetchNotifications, fetchUnreadCount, markAllAsRead } from '../../store/slices/notificationSlice';

const NotificationBell = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const { notifications, unreadCount, loading } = useSelector(state => state.notifications || {});

  const [serviceUp, setServiceUp] = useState(true);

  useEffect(() => {
    if (!serviceUp) return;
    const poll = () => {
      dispatch(fetchUnreadCount()).unwrap().catch(err => {
        if (err === 'service_unavailable') setServiceUp(false);
      });
    };
    poll();
    const interval = setInterval(poll, 30000);
    return () => clearInterval(interval);
  }, [dispatch, serviceUp]);

  const handleOpen = () => {
    setOpen(true);
    if (!notifications || notifications.length === 0) {
      dispatch(fetchNotifications({ limit: 5 }));
    }
  };

  const handleMarkAllRead = () => {
    dispatch(markAllAsRead());
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  const handleNotifClick = (notif) => {
    setOpen(false);
    if (notif.deep_link) {
      navigate(notif.deep_link);
    }
  };

  return (
    <>
      <IconButton color="inherit" ref={anchorRef} onClick={handleOpen}>
        <Badge badgeContent={unreadCount || 0} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxHeight: 400 } }}
      >
        <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={600}>Notifications</Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllRead}>Mark all read</Button>
          )}
        </Box>
        <Divider />

        {(!notifications || notifications.length === 0) && !loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No notifications yet</Typography>
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 280, overflow: 'auto' }}>
            {(notifications || []).slice(0, 5).map((notif) => (
              <ListItem
                key={notif.id}
                button
                onClick={() => handleNotifClick(notif)}
                sx={{
                  bgcolor: notif.is_read ? 'transparent' : 'action.hover',
                  borderLeft: notif.is_read ? 'none' : '3px solid',
                  borderColor: 'primary.main',
                }}
              >
                <ListItemText
                  primary={notif.title}
                  secondary={notif.body}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: notif.is_read ? 400 : 600 }}
                  secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Divider />
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button size="small" onClick={handleViewAll}>View all notifications</Button>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
